import { type AskPunttReview } from "@mg/schemas/src/christo/askPunttReview";
import { LinkSimple } from "@phosphor-icons/react";
import { Button, IconButton } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { type ReviewVersionOption, VersionPicker } from "./VersionPicker";

type AskV2ReviewCompareBarProps = {
  /** The version shown beside the open review, once it has loaded. */
  comparedReview: AskPunttReview | undefined;
  comparedOptions: readonly ReviewVersionOption[];
  comparedMark?: string;
  onSelectCompared: (reviewId: string) => void;
  /** The open review. */
  openReview: AskPunttReview;
  openOptions: readonly ReviewVersionOption[];
  openMark?: string;
  onSelectOpen: (reviewId: string) => void;
  /** The open review is the earlier version: its picker goes on the left. */
  openIsEarlier: boolean;
  syncPanes: boolean;
  onSyncPanesChange: (sync: boolean) => void;
  /** The viewer's rotate and zoom controls. */
  viewerControls: ReactNode;
  onExit: () => void;
};

/**
 * The one row over a version comparison: both versions' pickers, the earlier
 * one on the left, with the Sync panes toggle between them, the viewer
 * controls, and Exit compare.
 */
export function AskV2ReviewCompareBar({
  comparedReview,
  comparedOptions,
  comparedMark,
  onSelectCompared,
  openReview,
  openOptions,
  openMark,
  onSelectOpen,
  openIsEarlier,
  syncPanes,
  onSyncPanesChange,
  viewerControls,
  onExit,
}: AskV2ReviewCompareBarProps) {
  const { t } = useTranslation("ui");
  const comparedPicker = comparedReview ? (
    <VersionPicker
      review={comparedReview}
      options={comparedOptions}
      onSelect={onSelectCompared}
      testIdPrefix="ask-v2-review-compare-bar-compared-version"
      label={t("askV2.review.compareOtherPickerLabel")}
      mark={comparedMark}
    />
  ) : null;
  const openPicker = (
    <VersionPicker
      review={openReview}
      options={openOptions}
      onSelect={onSelectOpen}
      testIdPrefix="ask-v2-review-compare-bar-open-version"
      mark={openMark}
    />
  );
  return (
    <div
      role="toolbar"
      aria-label={t("askV2.review.compareBarLabel")}
      className="flex min-w-0 flex-1 items-center gap-3"
      data-testid="ask-v2-review-compare-bar"
    >
      {openIsEarlier ? openPicker : comparedPicker}
      <IconButton
        className="shrink-0"
        variant={syncPanes ? "soft" : "ghost"}
        color="gray"
        size="1"
        aria-label={t("askV2.review.compareSyncPanes")}
        aria-pressed={syncPanes}
        title={t("askV2.review.compareSyncPanes")}
        data-testid="ask-v2-review-compare-sync-toggle"
        onClick={() => onSyncPanesChange(!syncPanes)}
      >
        <LinkSimple size={14} weight="bold" aria-hidden="true" />
      </IconButton>
      {openIsEarlier ? comparedPicker : openPicker}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {viewerControls}
        <Button
          variant="solid"
          color="gray"
          highContrast
          size="1"
          data-testid="ask-v2-review-compare-exit"
          onClick={onExit}
        >
          {t("askV2.review.compareExit")}
        </Button>
      </div>
    </div>
  );
}
